/**
 * Email Broadcast Dashboard
 * Main dashboard page for the self-contained email broadcast system
 * Replicates Sendy functionality
 *
 * Created: January 3, 2026
 */

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col, Card } from "react-bootstrap";
import { Tabs, TabList, Tab, TabPanel } from "react-tabs";
import { useNavigate } from "react-router-dom";
import "react-tabs/style/react-tabs.css";

import { selectUser } from "../../../components/auth/auth.selector";
import HelmetWrapper from "../../../HelmetWrapper";
import ReactVortoxSpinner from "../../../components/common-components/ReactVortoxSpinner";

import {
  fetchEmailCampaignsData,
  fetchEmailListsData,
  fetchEmailTemplatesData,
  fetchSESQuotaData,
  fetchEmailAnalyticsData,
} from "../../../redux/email-broadcast/emailBroadcastSlice";

// Stat Card Component
const StatCard = ({ title, value, subtitle, color = "#D2000B", icon }) => (
  <Card className="stat-card mb-3" style={{ borderLeft: `4px solid ${color}` }}>
    <Card.Body>
      <div className="d-flex justify-content-between align-items-start">
        <div>
          <h6 className="text-muted mb-1">{title}</h6>
          <h3 className="mb-0" style={{ color }}>{value}</h3>
          {subtitle && <small className="text-muted">{subtitle}</small>}
        </div>
        {icon && <div className="stat-icon">{icon}</div>}
      </div>
    </Card.Body>
  </Card>
);

// Quick Action Button
const QuickAction = ({ title, subtitle, onClick, icon, color = "#D2000B" }) => (
  <Card
    className="quick-action-card mb-3"
    onClick={onClick}
    style={{ cursor: "pointer", transition: "all 0.2s" }}
    onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
    onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
  >
    <Card.Body className="d-flex align-items-center">
      <div
        className="action-icon me-3"
        style={{
          width: "48px",
          height: "48px",
          borderRadius: "8px",
          backgroundColor: `${color}15`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color,
        }}
      >
        {icon}
      </div>
      <div>
        <h6 className="mb-0">{title}</h6>
        <small className="text-muted">{subtitle}</small>
      </div>
    </Card.Body>
  </Card>
);

// Recent Campaigns Table
const RecentCampaigns = ({ campaigns, onViewCampaign }) => (
  <Card>
    <Card.Header className="d-flex justify-content-between align-items-center">
      <h6 className="mb-0">Recent Campaigns</h6>
    </Card.Header>
    <Card.Body className="p-0">
      <div className="table-responsive">
        <table className="table table-hover mb-0">
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Status</th>
              <th>Sent</th>
              <th>Opens</th>
              <th>Clicks</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {campaigns?.slice(0, 5).map((campaign) => (
              <tr
                key={campaign.uid}
                onClick={() => onViewCampaign(campaign)}
                style={{ cursor: "pointer" }}
              >
                <td>
                  <span className="fw-medium">{campaign.campaign_name}</span>
                </td>
                <td>
                  <span
                    className={`badge bg-${
                      campaign.campaign_status === "sent"
                        ? "success"
                        : campaign.campaign_status === "sending"
                        ? "warning"
                        : campaign.campaign_status === "draft"
                        ? "secondary"
                        : campaign.campaign_status === "scheduled"
                        ? "info"
                        : "danger"
                    }`}
                  >
                    {campaign.campaign_status}
                  </span>
                </td>
                <td>{campaign.sent_count || 0}</td>
                <td>
                  {campaign.unique_open_count || 0}
                  {campaign.sent_count > 0 && (
                    <small className="text-muted ms-1">
                      ({((campaign.unique_open_count / campaign.sent_count) * 100).toFixed(1)}%)
                    </small>
                  )}
                </td>
                <td>
                  {campaign.unique_click_count || 0}
                  {campaign.sent_count > 0 && (
                    <small className="text-muted ms-1">
                      ({((campaign.unique_click_count / campaign.sent_count) * 100).toFixed(1)}%)
                    </small>
                  )}
                </td>
                <td>
                  <small>{campaign.date_added}</small>
                </td>
              </tr>
            ))}
            {(!campaigns || campaigns.length === 0) && (
              <tr>
                <td colSpan="6" className="text-center text-muted py-4">
                  No campaigns yet. Create your first campaign!
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card.Body>
  </Card>
);

// SES Quota Card
const SESQuotaCard = ({ quota, loading }) => (
  <Card>
    <Card.Header>
      <h6 className="mb-0">AWS SES Quota</h6>
    </Card.Header>
    <Card.Body>
      {loading ? (
        <div className="text-center py-3">
          <ReactVortoxSpinner />
        </div>
      ) : quota ? (
        <>
          <div className="mb-3">
            <div className="d-flex justify-content-between mb-1">
              <small>Daily Usage</small>
              <small>{quota.percentUsed}%</small>
            </div>
            <div className="progress" style={{ height: "8px" }}>
              <div
                className="progress-bar"
                style={{
                  width: `${quota.percentUsed}%`,
                  backgroundColor: quota.percentUsed > 80 ? "#dc3545" : "#28a745",
                }}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-6">
              <small className="text-muted d-block">Sent Today</small>
              <strong>{quota.sentLast24Hours?.toLocaleString()}</strong>
            </div>
            <div className="col-6">
              <small className="text-muted d-block">Daily Limit</small>
              <strong>{quota.max24HourSend?.toLocaleString()}</strong>
            </div>
          </div>
          <hr />
          <div className="row">
            <div className="col-6">
              <small className="text-muted d-block">Remaining</small>
              <strong className="text-success">{quota.remaining?.toLocaleString()}</strong>
            </div>
            <div className="col-6">
              <small className="text-muted d-block">Max Rate</small>
              <strong>{quota.maxSendRate}/sec</strong>
            </div>
          </div>
        </>
      ) : (
        <p className="text-muted mb-0">Unable to fetch SES quota</p>
      )}
    </Card.Body>
  </Card>
);

const EmailBroadcastDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userInfoData = useSelector(selectUser);

  const {
    emailCampaigns,
    emailCampaignsLoading,
    emailLists,
    emailListsLoading,
    emailTemplates,
    emailTemplatesLoading,
    sesQuota,
    sesQuotaLoading,
    emailAnalytics,
    emailAnalyticsLoading,
  } = useSelector((state) => state.emailBroadcast || {});

  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    if (userInfoData?.token) {
      // Fetch all data on mount
      dispatch(fetchEmailCampaignsData({ filters: { limit: 10 }, token: userInfoData.token }));
      dispatch(fetchEmailListsData({ filters: {}, token: userInfoData.token }));
      dispatch(fetchEmailTemplatesData({ filters: {}, token: userInfoData.token }));
      dispatch(fetchSESQuotaData({ token: userInfoData.token }));
      dispatch(fetchEmailAnalyticsData({ filters: {}, token: userInfoData.token }));
    }
  }, [dispatch, userInfoData?.token]);

  // Calculate stats
  const totalLists = emailLists?.data?.length || 0;
  const totalSubscribers = emailLists?.data?.reduce((acc, list) => acc + (list.subscriber_count || 0), 0) || 0;
  const totalTemplates = emailTemplates?.data?.length || 0;
  const totalCampaigns = emailCampaigns?.pagination?.totalRecords || 0;
  const sentCampaigns = emailCampaigns?.data?.filter(c => c.campaign_status === 'sent').length || 0;

  const handleViewCampaign = (campaign) => {
    navigate(`/broadcast-system/email-broadcast/campaigns/${campaign.uid}`);
  };

  return (
    <HelmetWrapper title="WTF: Email Broadcast">
      <Container className="bg-cloud-k px-50px gobal-container" fluid>
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-1">Email Broadcast</h4>
                <p className="text-muted mb-0">Send targeted email campaigns to your members</p>
              </div>
              <button
                className="btn btn-primary"
                style={{ backgroundColor: "#D2000B", borderColor: "#D2000B" }}
                onClick={() => navigate("/broadcast-system/email-broadcast/campaigns/new")}
              >
                + Create Campaign
              </button>
            </div>
          </Col>
        </Row>

        {/* Stats Row */}
        <Row className="mb-4">
          <Col md={3}>
            <StatCard
              title="Total Lists"
              value={totalLists}
              subtitle={`${totalSubscribers.toLocaleString()} subscribers`}
              color="#2196F3"
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z" fill="#2196F3"/>
                </svg>
              }
            />
          </Col>
          <Col md={3}>
            <StatCard
              title="Templates"
              value={totalTemplates}
              subtitle="Ready to use"
              color="#4CAF50"
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z" fill="#4CAF50"/>
                </svg>
              }
            />
          </Col>
          <Col md={3}>
            <StatCard
              title="Campaigns"
              value={totalCampaigns}
              subtitle={`${sentCampaigns} sent`}
              color="#FF9800"
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="#FF9800"/>
                </svg>
              }
            />
          </Col>
          <Col md={3}>
            <StatCard
              title="Emails Sent Today"
              value={sesQuota?.sentLast24Hours?.toLocaleString() || "0"}
              subtitle={`of ${sesQuota?.max24HourSend?.toLocaleString() || "100,000"}`}
              color="#D2000B"
              icon={
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="#D2000B"/>
                </svg>
              }
            />
          </Col>
        </Row>

        <Row>
          {/* Main Content */}
          <Col md={8}>
            {/* Quick Actions */}
            <h6 className="mb-3">Quick Actions</h6>
            <Row className="mb-4">
              <Col md={4}>
                <QuickAction
                  title="Create Campaign"
                  subtitle="Start a new email campaign"
                  icon={<span className="fs-5">+</span>}
                  onClick={() => navigate("/broadcast-system/email-broadcast/campaigns/new")}
                />
              </Col>
              <Col md={4}>
                <QuickAction
                  title="Manage Lists"
                  subtitle="View & edit subscriber lists"
                  icon={<span className="fs-5">&#128203;</span>}
                  color="#2196F3"
                  onClick={() => navigate("/broadcast-system/email-broadcast/lists")}
                />
              </Col>
              <Col md={4}>
                <QuickAction
                  title="Email Templates"
                  subtitle="Create & manage templates"
                  icon={<span className="fs-5">&#128196;</span>}
                  color="#4CAF50"
                  onClick={() => navigate("/broadcast-system/email-broadcast/templates")}
                />
              </Col>
            </Row>

            {/* Recent Campaigns */}
            {emailCampaignsLoading ? (
              <div className="text-center py-5">
                <ReactVortoxSpinner />
              </div>
            ) : (
              <RecentCampaigns
                campaigns={emailCampaigns?.data}
                onViewCampaign={handleViewCampaign}
              />
            )}
          </Col>

          {/* Sidebar */}
          <Col md={4}>
            {/* SES Quota */}
            <SESQuotaCard quota={sesQuota} loading={sesQuotaLoading} />

            {/* Navigation Links */}
            <Card className="mt-3">
              <Card.Header>
                <h6 className="mb-0">Navigation</h6>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="list-group list-group-flush">
                  <a
                    href="/broadcast-system/email-broadcast/campaigns"
                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                  >
                    All Campaigns
                    <span className="badge bg-primary">{totalCampaigns}</span>
                  </a>
                  <a
                    href="/broadcast-system/email-broadcast/lists"
                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                  >
                    Subscriber Lists
                    <span className="badge bg-info">{totalLists}</span>
                  </a>
                  <a
                    href="/broadcast-system/email-broadcast/templates"
                    className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                  >
                    Email Templates
                    <span className="badge bg-success">{totalTemplates}</span>
                  </a>
                  <a
                    href="/broadcast-system/email-broadcast/suppression"
                    className="list-group-item list-group-item-action"
                  >
                    Suppression List
                  </a>
                  <a
                    href="/broadcast-system/email-broadcast/analytics"
                    className="list-group-item list-group-item-action"
                  >
                    Analytics
                  </a>
                </div>
              </Card.Body>
            </Card>

            {/* Tips Card */}
            <Card className="mt-3">
              <Card.Header style={{ backgroundColor: "#FFF3CD" }}>
                <h6 className="mb-0">Tips for Better Delivery</h6>
              </Card.Header>
              <Card.Body>
                <ul className="small mb-0 ps-3">
                  <li className="mb-1">Keep 60% text / 40% images ratio</li>
                  <li className="mb-1">Avoid spam trigger words in subject</li>
                  <li className="mb-1">Always include unsubscribe link</li>
                  <li className="mb-1">Test on mobile before sending</li>
                  <li>Send at optimal times (10AM-2PM IST)</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </HelmetWrapper>
  );
};

export default EmailBroadcastDashboard;
